import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ClientPicker from "../ClientPicker";
import AddressAutocomplete from "../AddressAutocomplete";

export const OOH_CATEGORIES = ["Heating / Boiler", "Plumbing", "Electrical", "Roof / Gutters", "Doors / Windows", "Locks / Security", "Pest Control", "Garden / External", "Damp / Mould", "Appliance", "General"];

export const emptyCase = () => ({ title: "", category: "General", description: "", requester_name: "", requester_phone: "", requester_email: "", client_id: "", client_name: "",
  street_address: "", town_city: "", county: "", postcode: "", latitude: null, longitude: null, severity_hint: "" });

export function fromCase(c) {
  return { title: c.title || "", category: c.category || "General", description: c.description || "", requester_name: c.requester?.name || "", requester_phone: c.requester?.phone || "",
    requester_email: c.requester?.email || "", client_id: c.client_id || "", client_name: c.client_name || "", street_address: c.address?.street_address || "", town_city: c.address?.town_city || "",
    county: c.address?.county || "", postcode: c.address?.postcode || "", latitude: c.address?.latitude ?? null, longitude: c.address?.longitude ?? null, severity_hint: c.severity_hint || "" };
}

export function OohCaseForm({ f, set, prefix = "ooh-form" }) {
  const up = (k) => (e) => set({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-[1fr_200px] gap-3">
        <div><label className="text-xs font-medium">What's the problem? *</label><Input value={f.title} onChange={up("title")} placeholder="e.g. No heating or hot water, boiler showing F22" data-testid={`${prefix}-title`} /></div>
        <div>
          <label className="text-xs font-medium">Category</label>
          <Select value={f.category} onValueChange={(v) => set({ ...f, category: v })}>
            <SelectTrigger data-testid={`${prefix}-category`}><SelectValue /></SelectTrigger>
            <SelectContent>{OOH_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><label className="text-xs font-medium">Details</label><Textarea rows={3} value={f.description} onChange={up("description")} placeholder="What the caller told you — when it started, what they've tried, anyone vulnerable in the home…" data-testid={`${prefix}-description`} /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className="text-xs font-medium">Caller name *</label><Input value={f.requester_name} onChange={up("requester_name")} data-testid={`${prefix}-requester-name`} /></div>
        <div><label className="text-xs font-medium">Phone *</label><Input value={f.requester_phone} onChange={up("requester_phone")} data-testid={`${prefix}-requester-phone`} /></div>
        <div><label className="text-xs font-medium">Email</label><Input value={f.requester_email} onChange={up("requester_email")} placeholder="for the steps email" data-testid={`${prefix}-requester-email`} /></div>
      </div>
      <ClientPicker value={f} onChange={set} testIdPrefix={`${prefix}-client`} manualFields={false} />
      <div>
        <AddressAutocomplete label="Property address" value={f.street_address} onChange={(v) => set({ ...f, street_address: v })}
          onSelect={(a) => set({ ...f, street_address: a.street_address || f.street_address, town_city: a.town_city || "", county: a.county || "", postcode: a.postcode || "", latitude: a.latitude ?? null, longitude: a.longitude ?? null })}
          data-testid={`${prefix}-address`} />
        <div className="grid grid-cols-3 gap-2 mt-2">
          <Input value={f.town_city} onChange={up("town_city")} placeholder="Town / city" data-testid={`${prefix}-town`} />
          <Input value={f.county} onChange={up("county")} placeholder="County" data-testid={`${prefix}-county`} />
          <Input value={f.postcode} onChange={up("postcode")} placeholder="Postcode" data-testid={`${prefix}-postcode`} />
        </div>
      </div>
    </div>
  );
}

export default function OohEditCaseDialog({ c, open, onOpenChange, onChanged }) {
  const [f, setF] = useState(() => fromCase(c));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(fromCase(c)); }, [open, c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { await API.put(`/ooh/cases/${c.id}`, f); toast.success("Case updated"); onOpenChange(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ooh-edit-dialog">
        <DialogHeader><DialogTitle>Edit {c.reference}</DialogTitle></DialogHeader>
        <OohCaseForm f={f} set={setF} prefix="ooh-edit" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="ooh-edit-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
